function validateBranch(branch) {
  if (!branch || typeof branch !== "object" || typeof branch.id !== "string" || !branch.id || !Array.isArray(branch.options) || branch.options.length === 0) throw new TypeError("Expander branch is invalid.");
  const optionIds = new Set();
  if (branch.ownerChoiceIds !== undefined && !Array.isArray(branch.ownerChoiceIds)) throw new TypeError(`Expander branch ${branch.id} ownership is invalid.`);
  const matchKeys = new Set();
  for (const option of branch.options) {
    if (!option || typeof option !== "object" || typeof option.id !== "string" || !option.id || typeof option.matchKey !== "string" || !option.matchKey) throw new TypeError(`Expander branch ${branch.id} contains an invalid option.`);
    if (optionIds.has(option.id)) throw new TypeError(`Expander branch ${branch.id} contains duplicate option ${option.id}.`);
    if (matchKeys.has(option.matchKey)) throw new TypeError(`Expander branch ${branch.id} contains duplicate Match Key ${option.matchKey}.`);
    optionIds.add(option.id);
    matchKeys.add(option.matchKey);
  }
  return branch;
}

const contributions = (value) => Array.isArray(value) ? value.filter((entry) => typeof entry === "string" && entry) : [];

function activeBranches(branches, selections) {
  const active = new Set(branches.filter((branch) => contributions(branch.ownerChoiceIds).length === 0).map((branch) => branch.id));
  let changed = true;
  while (changed) {
    changed = false;
    const activeOptions = new Set(branches.filter((branch) => active.has(branch.id)).map((branch) => selections.get(branch.id)));
    for (const branch of branches) {
      if (active.has(branch.id) || !contributions(branch.ownerChoiceIds).some((id) => activeOptions.has(id))) continue;
      active.add(branch.id); changed = true;
    }
  }
  return active;
}

function snapshot(branches, selections) {
  const selectedMeshes = new Set();
  const selectedShaders = new Set();
  const selectedMatchKeys = new Set();
  const selectedTransforms = new Map();
  const active = activeBranches(branches, selections);
  for (const branch of branches.filter((candidate) => active.has(candidate.id))) {
    const optionId = selections.get(branch.id);
    const option = branch.options.find((candidate) => candidate.id === optionId);
    if (!option) throw new Error(`Expander branch ${branch.id} has no selected option.`);
    contributions(option.selectedMeshes).forEach((id) => selectedMeshes.add(id));
    contributions(option.selectedShaders).forEach((id) => selectedShaders.add(id));
    (Array.isArray(option.selectedTransforms) ? option.selectedTransforms : []).forEach((transform) => {
      if (transform && typeof transform.id === "string") selectedTransforms.set(transform.id, transform);
    });
    selectedMatchKeys.add(option.matchKey);
  }
  return {
    selections: Object.fromEntries(selections),
    selectedMeshes: [...selectedMeshes],
    selectedShaders: [...selectedShaders],
    selectedTransforms: [...selectedTransforms.values()],
    selectedMatchKeys: [...selectedMatchKeys],
    activeBranchIds: [...active],
  };
}

export function createExpanderSession(input) {
  if (!input || typeof input !== "object" || !Array.isArray(input.branches) || input.branches.length === 0) throw new TypeError("Expander branches are required.");
  const branches = input.branches.map(validateBranch);
  const branchIds = new Set();
  branches.forEach((branch) => {
    if (branchIds.has(branch.id)) throw new TypeError(`Duplicate Expander branch: ${branch.id}`);
    branchIds.add(branch.id);
  });
  const requested = input.initialSelections && typeof input.initialSelections === "object" ? input.initialSelections : {};
  const selections = new Map();
  const activeMatchKeys = new Set();
  for (const branch of branches) {
    const requestedOption = branch.options.find((option) => option.id === requested[branch.id]);
    const correspondingOptions = branch.options.filter((option) => activeMatchKeys.has(option.matchKey));
    if (correspondingOptions.length > 1) throw new Error(`Expander branch ${branch.id} has more than one option matching current selection.`);
    const option = requestedOption ?? correspondingOptions[0] ?? branch.options[0];
    selections.set(branch.id, option.id);
    activeMatchKeys.add(option.matchKey);
  }
  return {
    get state() { return snapshot(branches, selections); },
    select(branchId, optionId) {
      const branch = branches.find((candidate) => candidate.id === branchId);
      if (!branch) throw new Error(`Unknown Expander branch: ${branchId}`);
      const option = branch.options.find((candidate) => candidate.id === optionId);
      if (!option) throw new Error(`Unknown option ${optionId} in Expander branch ${branchId}.`);
      selections.set(branchId, option.id);
      const active = activeBranches(branches, selections);
      branches.forEach((candidate) => {
        if (!active.has(candidate.id)) return;
        if (candidate.id === branch.id) return;
        const corresponding = candidate.options.find((entry) => entry.matchKey === option.matchKey);
        if (corresponding) selections.set(candidate.id, corresponding.id);
      });
      return snapshot(branches, selections);
    },
  };
}
